import type { SignedOutReason } from './auth-events';
import type { PortalProfile } from './generated/types.gen';
import type { Tokens } from './token-store';

/**
 * How the tabs of one browser share one sign-in (portal-session-rules, D4,
 * design Decision 1).
 *
 * Each tab keeps its sign-in in its own sessionStorage, which dies with the
 * tab - the right lifetime for an administration tool. On its own that made
 * every tab a separate sign-in, and with one session per person a second tab
 * would sign the first out. So the tabs talk over one BroadcastChannel:
 *
 * - a new tab with no sign-in asks, and an open tab answers with its own;
 * - a tab that renews the tokens passes the new pair on, so no tab presents
 *   a refresh token another has already retired (review item 40);
 * - a tab whose session ends tells the others, so they end too.
 *
 * Messages stay within one origin; any script there could already read
 * sessionStorage, so passing tokens this way exposes nothing new (see the
 * security note in token-store.ts).
 */

const NAME = 'activistarc-portal-session';

/** Identifies this tab, so it can ignore what its own channels send. */
const THIS_TAB = crypto.randomUUID();

/**
 * What one tab holds. Staff "view portal" mode is deliberately not in it (D7).
 * The person is whatever the answering tab keeps; the asking tab checks it
 * before trusting it, as it does a person stored by an older build.
 */
export interface SessionSnapshot {
  tokens: Tokens;
  user: unknown;
  organizationId: string | null;
}

export interface RenewedTokens {
  tokens: Tokens;
  /**
   * The person as the renewal reply describes them. Also whose tokens these
   * are: a tab takes them only for the person it shows (P1).
   */
  profile: PortalProfile | null;
}

/** A session that ended in another tab, and whose it was. */
export interface EndedElsewhere {
  reason: SignedOutReason;
  userId: string | null;
}

type Message =
  | { type: 'ask'; from: string; id: string }
  | ({ type: 'answer'; from: string; id: string } & SessionSnapshot)
  | ({ type: 'tokens'; from: string } & RenewedTokens)
  | ({ type: 'signed-out'; from: string } & EndedElsewhere);

export interface SessionChannel {
  /** False where the browser has no BroadcastChannel: then nothing is sent or heard. */
  available: boolean;
  /** Asks the other tabs for their sign-in; the first answer, or null after `timeoutMs`. */
  ask: (timeoutMs: number) => Promise<SessionSnapshot | null>;
  /** Answers other tabs' asks with this tab's sign-in, when it has one (I1). */
  answerWith: (getSnapshot: () => SessionSnapshot | null) => () => void;
  postTokens: (tokens: Tokens, profile: PortalProfile | null) => void;
  postSignedOut: (reason: SignedOutReason, userId: string | null) => void;
  onTokens: (listener: (renewed: RenewedTokens) => void) => () => void;
  onSignedOut: (listener: (ended: EndedElsewhere) => void) => () => void;
  close: () => void;
}

/** Whether this browser lets tabs talk; without it each tab keeps its own sign-in. */
export function canShareSessions(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

const UNAVAILABLE: SessionChannel = {
  available: false,
  ask: () => Promise.resolve(null),
  answerWith: () => () => undefined,
  postTokens: () => undefined,
  postSignedOut: () => undefined,
  onTokens: () => () => undefined,
  onSignedOut: () => () => undefined,
  close: () => undefined,
};

/**
 * Opens this tab's end of the channel. `tabId` is for tests that play two
 * tabs in one page; the app always uses this tab's own id.
 */
export function createSessionChannel({
  tabId = THIS_TAB,
}: { tabId?: string } = {}): SessionChannel {
  if (!canShareSessions()) return UNAVAILABLE;

  const channel = new BroadcastChannel(NAME);

  const post = (message: Message) => {
    try {
      channel.postMessage(message);
    } catch {
      // A closed channel: the tab is going away, and so is its sign-in.
    }
  };

  /** Listens to messages from other tabs only. */
  const listen = (handle: (message: Message) => void) => {
    const listener = (event: MessageEvent) => {
      const message = event.data as Message | null;
      if (!message || typeof message !== 'object' || message.from === tabId) return;
      handle(message);
    };
    channel.addEventListener('message', listener);
    return () => channel.removeEventListener('message', listener);
  };

  return {
    available: true,

    ask(timeoutMs) {
      const id = crypto.randomUUID();
      return new Promise((resolve) => {
        let stop = () => undefined as void;
        const timer = setTimeout(() => {
          stop();
          resolve(null);
        }, timeoutMs);
        stop = listen((message) => {
          if (message.type !== 'answer' || message.id !== id) return;
          clearTimeout(timer);
          stop();
          resolve({
            tokens: message.tokens,
            user: message.user,
            organizationId: message.organizationId,
          });
        });
        post({ type: 'ask', from: tabId, id });
      });
    },

    answerWith(getSnapshot) {
      return listen((message) => {
        if (message.type !== 'ask') return;
        const snapshot = getSnapshot();
        if (!snapshot) return;
        post({ type: 'answer', from: tabId, id: message.id, ...snapshot });
      });
    },

    postTokens(tokens, profile) {
      post({ type: 'tokens', from: tabId, tokens, profile });
    },

    postSignedOut(reason, userId) {
      post({ type: 'signed-out', from: tabId, reason, userId });
    },

    onTokens(listener) {
      return listen((message) => {
        if (message.type === 'tokens') {
          listener({ tokens: message.tokens, profile: message.profile });
        }
      });
    },

    onSignedOut(listener) {
      return listen((message) => {
        if (message.type === 'signed-out') {
          listener({ reason: message.reason, userId: message.userId });
        }
      });
    },

    close() {
      channel.close();
    },
  };
}
